import { useState, useRef, useCallback, useEffect } from 'react';
import {
  createAudioContext,
  ensureAudioContextRunning,
  decodeAudioToFloat32,
  createAudioBuffer,
  scheduleAudioPlayback,
  stopAllAudio,
  resetAudioQueue,
  processMicrophoneChunk
} from '../utilities/audioUtils';
import { STOP_PLAYBACK_EVENT } from '../utilities/playbackControl';
import useMicSilenceWatch from './useMicSilenceWatch';

/**
 * Custom hook for managing ElevenLabs conversations via Cloud Run proxy
 * Mirrors the @elevenlabs/react useConversation hook API for compatibility
 *
 * This hook connects to our Cloud Run WebSocket proxy instead of directly to ElevenLabs,
 * keeping prompts secure on the server side.
 *
 * Implements full audio pipeline:
 * - Microphone capture via Web Audio API
 * - PCM16 encoding at 16kHz mono
 * - WebSocket streaming to/from proxy
 * - Audio playback
 */
export const useElevenLabsConversation = (config = {}) => {
  const {
    micMuted = false,
    onConnect = () => {},
    onDisconnect = () => {},
    onMessage = () => {},
    onUpdateMessage = () => {}, // For tentative/streaming messages
    onError = () => {},
    onDebug = () => {},
    onModeChange = () => {},
    onStatusChange = () => {},
  } = config;

  // State
  const [status, setStatus] = useState('disconnected');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [mode, setMode] = useState('listening');

  // Refs for WebSocket and audio management
  const wsRef = useRef(null);
  const conversationIdRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioWorkletNodeRef = useRef(null);
  const nextStartTimeRef = useRef(0);
  const audioQueueRef = useRef(new Set());

  // Internal state tracking
  const isMutedRef = useRef(micMuted);
  // Counts audio graph callbacks, not sent frames — a muted mic is still a live pipeline.
  const micCallbackCountRef = useRef(0);

  // Store latest callbacks in refs to avoid stale closures
  const onMessageRef = useRef(onMessage);
  const onUpdateMessageRef = useRef(onUpdateMessage);
  const onErrorRef = useRef(onError);
  const onDebugRef = useRef(onDebug);
  const onModeChangeRef = useRef(onModeChange);

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onUpdateMessageRef.current = onUpdateMessage;
    onErrorRef.current = onError;
    onDebugRef.current = onDebug;
    onModeChangeRef.current = onModeChange;
  }, [onMessage, onUpdateMessage, onError, onDebug, onModeChange]);

  // Update muted state when prop changes
  useEffect(() => {
    isMutedRef.current = micMuted;
    onDebugRef.current({ type: 'mute_state_change', muted: micMuted });
  }, [micMuted]);

  const stopPlayback = useCallback((reason = 'manual') => {
    stopAllAudio({ nextStartTimeRef, audioQueueRef });
    setIsSpeaking(false);
    setMode('listening');
    onModeChangeRef.current({ mode: 'listening' });
    onDebugRef.current({ type: 'playback_stopped', reason });
  }, []);

  useEffect(() => {
    const handleStopPlayback = (event) => {
      stopPlayback(event?.detail?.reason || 'global-event');
    };

    window.addEventListener(STOP_PLAYBACK_EVENT, handleStopPlayback);
    return () => window.removeEventListener(STOP_PLAYBACK_EVENT, handleStopPlayback);
  }, [stopPlayback]);

  /**
   * Initialize audio for microphone capture
   */
  const initializeAudio = useCallback(async () => {
    try {
      micCallbackCountRef.current = 0;

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 16000, // 16kHz for ElevenLabs
        }
      });

      audioStreamRef.current = stream;

      // Create audio context
      audioContextRef.current = createAudioContext(16000);

      // A context that comes up suspended silently captures nothing. Start it before
      // anything is hung off it, and fail here rather than halfway through a session.
      await ensureAudioContextRunning(audioContextRef.current);

      // Create audio source from microphone
      const source = audioContextRef.current.createMediaStreamSource(stream);

      // Create script processor for audio chunks
      const processor = audioContextRef.current.createScriptProcessor(2048, 1, 1);

      processor.onaudioprocess = (e) => {
        // Recorded before the mute check: this counts pipeline health, not speech.
        micCallbackCountRef.current += 1;

        // Only send audio if not muted and WebSocket is open
        if (isMutedRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          return;
        }

        const inputData = e.inputBuffer.getChannelData(0);

        // Process microphone chunk (Float32 → PCM16 → base64)
        const base64Audio = processMicrophoneChunk(inputData);

        // Send audio chunk to proxy
        wsRef.current.send(JSON.stringify({
          type: 'audio_input',
          data: base64Audio
        }));
      };

      source.connect(processor);
      processor.connect(audioContextRef.current.destination);
      audioWorkletNodeRef.current = processor;

      onDebugRef.current({ type: 'audio_initialized' });
    } catch (error) {
      console.error('Error initializing audio:', error);
      // "Access denied" is only one of the ways this fails — a context that refuses to
      // start is the other, and it needs its own message rather than sending the learner
      // to check a permission they already granted.
      const isPermission = error?.name === 'NotAllowedError' || error?.name === 'SecurityError';
      onErrorRef.current(
        isPermission ? 'Microphone access denied' : 'Microphone could not be started',
        { type: 'audio_init_error', reason: error?.name, audioContextState: error?.state }
      );
      throw error;
    }
  }, []);

  /**
   * Armed once the agent is live, because that is the point from which the microphone's
   * silence starts costing the learner the session. If nothing has come through the graph
   * by the time this fires, no amount of speaking will help — saying so beats leaving
   * them talking to a microphone that isn't listening.
   */
  const { start: startMicSilenceWatch, stop: stopMicSilenceWatch } = useMicSilenceWatch({
    isPipelineAlive: useCallback(() => micCallbackCountRef.current > 0, []),
    onSilent: useCallback(() => {
      const audioContextState = audioContextRef.current?.state;
      console.error('Microphone pipeline produced no audio; the session cannot hear the learner');
      onDebugRef.current({ type: 'mic_pipeline_dead', audioContextState });
      onErrorRef.current('Microphone is not sending any audio', {
        type: 'mic_pipeline_dead',
        audioContextState,
      });
    }, []),
  });

  /**
   * Play audio chunk received from ElevenLabs
   */
  const playAudioChunk = useCallback(async (base64Audio) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = createAudioContext(16000); // Match ElevenLabs agent PCM 16kHz output
      }

      // Playback hangs off the same context as capture, so it fails the same silent way
      // if that context is suspended — the agent appears mute rather than broken.
      await ensureAudioContextRunning(audioContextRef.current);

      // Decode base64 audio to Float32
      const float32Array = decodeAudioToFloat32(base64Audio);

      if (float32Array.length === 0) {
        onDebugRef.current({ type: 'audio_chunk_empty' });
        return;
      }

      // Create audio buffer at 16kHz (PCM16 16000Hz format from ElevenLabs)
      const audioBuffer = createAudioBuffer(audioContextRef.current, float32Array, 16000);

      // Schedule audio with gapless playback
      scheduleAudioPlayback(
        audioContextRef.current,
        audioBuffer,
        { nextStartTimeRef, audioQueueRef },
        (isQueueEmpty) => {
          // Only set to listening if queue is empty
          if (isQueueEmpty) {
            setIsSpeaking(false);
            setMode('listening');
            onModeChangeRef.current({ mode: 'listening' });
          }
        }
      );

      // Set speaking state when first chunk plays
      if (audioQueueRef.current.size > 0) {
        setIsSpeaking(true);
        setMode('speaking');
        onModeChangeRef.current({ mode: 'speaking' });
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      onErrorRef.current('Audio playback error', { type: 'audio_playback_error' });
    }
  }, []);

  /**
   * Handle incoming WebSocket messages from proxy
   */
  const handleWebSocketMessage = useCallback((event) => {
    try {
      // Check if data is a Blob (binary audio) or string (JSON)
      if (event.data instanceof Blob) {
        // Binary audio data from ElevenLabs - convert to base64 and play
        onDebugRef.current({ type: 'audio_blob_received', size: event.data.size });

        const reader = new FileReader();
        reader.onload = () => {
          const arrayBuffer = reader.result;
          const uint8Array = new Uint8Array(arrayBuffer);
          const base64 = btoa(String.fromCharCode(...uint8Array));
          playAudioChunk(base64);
        };
        reader.readAsArrayBuffer(event.data);
        return;
      }

      // Parse JSON messages
      const data = JSON.parse(event.data);

      onDebugRef.current({ type: 'websocket_message', data });

      // Handle different message types from proxy
      if (data.type === 'connected') {
        // Proxy confirmed connection is ready - AI agent is listening
        onDebugRef.current({ type: 'agent_ready' });

        // The agent is now listening, so from here its silence is our problem.
        startMicSilenceWatch();

        // Update status to indicate ready state (removes loading)
        setStatus('connected');
        onStatusChange({ status: 'connected' });
      } else if (data.type === 'audio') {
        // Audio response from agent - nested in audio_event
        const audioData = data.audio_event?.audio_base_64 || data.audio_event?.chunk;
        if (audioData) {
          playAudioChunk(audioData);
        }
      } else if (data.type === 'user_transcript') {
        // User speech was transcribed
        const transcript = data.user_transcription_event?.user_transcript;
        const isFinal = data.is_final !== false; // Default to true if field missing

        if (isFinal && transcript) {
          // Final transcript - create new message bubble
          onMessageRef.current({
            message: transcript,
            source: 'user',
          });
        } else if (transcript) {
          // Tentative transcript - update existing message bubble
          onUpdateMessageRef.current({
            message: transcript,
            source: 'user',
          });
        }
      } else if (data.type === 'agent_response') {
        // Agent text response
        const response = data.agent_response_event?.agent_response;
        const isFinal = data.is_final !== false; // Default to true if field missing

        if (isFinal && response) {
          // Final response - create new message bubble
          onMessageRef.current({
            message: response,
            source: 'ai',
          });
        } else if (response) {
          // Tentative response - update existing message bubble
          onUpdateMessageRef.current({
            message: response,
            source: 'ai',
          });
        }
      } else if (data.type === 'interruption') {
        onDebugRef.current({ type: 'interruption' });

        // Stop all queued audio on interruption
        stopPlayback('interruption');
      } else if (data.type === 'agent_is_speaking') {
        // Note: We handle speaking state via audio playback (source.onended)
        // This prevents conflicts between audio-based and message-based state changes
        onDebugRef.current({ type: 'agent_is_speaking', value: data.value });
      } else if (data.type === 'error') {
        console.error('❌ ElevenLabs Proxy Error:', data.message);
        onErrorRef.current(data.message, { type: 'elevenlabs_error', error: data.error });
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
      onErrorRef.current(error.message, { type: 'message_parsing_error' });
    }
  }, [playAudioChunk, onStatusChange, startMicSilenceWatch, stopPlayback]);

  /**
   * Start conversation session
   */
  const startSession = useCallback(async (sessionConfig = {}) => {
    try {
      stopPlayback('session-start');

      setStatus('connecting');
      onStatusChange({ status: 'connecting' });

      // Session config contains user context for server-side prompt generation
      const sessionConfigForProxy = sessionConfig;

      // Use existing Firestore doc ID as conversation ID (tied to Firebase object)
      const conversationId = sessionConfig.firestoreDocId;
      conversationIdRef.current = conversationId;

      // Connect to Cloud Run WebSocket Proxy instead of direct ElevenLabs
      // Proxy handles prompt generation and ElevenLabs connection server-side
      const { _config } = await import('../Constants');
      const proxyBaseUrl = _config.wsProxyUrl;
      const wsUrl = `${proxyBaseUrl.replace('https://', 'wss://')}/ws/elevenlabs`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        // Reset audio queue and timing for new session
        resetAudioQueue({ nextStartTimeRef, audioQueueRef });

        // Send init message to proxy with session configuration
        // Proxy will generate prompt server-side and establish agent connection
        const initMessage = {
          type: 'init',
          config: sessionConfigForProxy
        };

        ws.send(JSON.stringify(initMessage));

        // Initialize audio capture
        try {
          await initializeAudio();
        } catch (error) {
          console.error('Failed to initialize audio:', error);
          onErrorRef.current('Microphone access required', { type: 'audio_init_error' });
          // Without a microphone this session can only ever be an agent talking to
          // silence. Tearing the socket down turns that into a disconnect the UI already
          // recovers from, instead of a session that looks live for 60 seconds and then
          // dies on the agent's own timeout.
          ws.close();
          return;
        }

        // Keep status as 'connecting' until we receive 'connected' message from agent
        onConnect({ conversationId });
      };

      ws.onmessage = handleWebSocketMessage;

      ws.onerror = (error) => {
        console.error('WebSocket connection error:', error);
        onErrorRef.current('WebSocket connection error', { type: 'websocket_error', error });
      };

      ws.onclose = (event) => {
        stopMicSilenceWatch();
        setStatus('disconnected');
        onStatusChange({ status: 'disconnected' });
        onDisconnect();
      };

      return conversationId;
    } catch (error) {
      console.error('Failed to start conversation:', error);
      setStatus('disconnected');
      onStatusChange({ status: 'disconnected' });
      onErrorRef.current(error.message, { type: 'session_start_error' });
      throw error;
    }
  }, [handleWebSocketMessage, onConnect, onDisconnect, onStatusChange, initializeAudio, stopMicSilenceWatch, stopPlayback]);

  /**
   * End conversation session
   */
  const endSession = useCallback(async () => {
    setStatus('disconnecting');
    onStatusChange({ status: 'disconnecting' });

    // A session that is being torn down has no microphone left to complain about.
    stopMicSilenceWatch();
    stopPlayback('session-end');

    // Stop audio capture
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    // Disconnect audio nodes
    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.disconnect();
      audioWorkletNodeRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus('disconnected');
    onStatusChange({ status: 'disconnected' });
    onDisconnect();
  }, [onDisconnect, onStatusChange, stopMicSilenceWatch, stopPlayback]);

  return {
    status,
    isSpeaking,
    mode,
    startSession,
    endSession,
    stopPlayback,
  };
};
